import { Head } from "@inertiajs/react"
import { BookOpen, X } from "lucide-react"
import { useCallback, useMemo, useState } from "react"

import { Button } from "@/components/ui/button"
import { useIsMobile } from "@/hooks/use-mobile"
import ReplayLayout from "@/layouts/replay-layout"
import { getTeamHue } from "@/lib/race-colors"
import type { AnimationPlayer, ReplayPageProps } from "@/types"

import { getActionColor } from "./replay/action-colors"
import { NarrativePanel } from "./replay/narrative-panel"
import { PitchGrid } from "./replay/pitch-grid"
import { PlaybackControls } from "./replay/playback-controls"
import { buildPlayerLookup } from "./replay/player-name-links"
import { PlayerPopover } from "./replay/player-popover"
import { TurnIndicator } from "./replay/turn-indicator"
import { usePitchState } from "./replay/use-pitch-state"
import { useReplayReducer } from "./replay/use-replay-reducer"

// Bypass AppLayout — the default PersistentLayout (for Toaster/flash) applied by
// inertia.tsx is the only outer wrapper; ReplayLayout renders its own minimal chrome
// inside the page.

export default function ReplayPage({
  league,
  match,
  animation_data,
}: Omit<ReplayPageProps, "round" | "season">) {
  // ─── Resolved team hues ──────────────────────────────────────────────
  const homeTeamHue = getTeamHue(
    match.home_team_race,
    match.home_team_color_hue,
  )
  const awayTeamHue = getTeamHue(
    match.away_team_race,
    match.away_team_color_hue,
  )

  // ─── Initial pitch state (kickoff snapshot) ───────────────────────────
  const initialPitchState = usePitchState(animation_data)

  // ─── Unified replay reducer (pitch state, playback index, animation) ──
  const {
    state: replayState,
    derived: { currentEvent },
    speed,
    togglePlay,
    stepForward,
    stepBack,
    seekTo,
    setSpeed,
  } = useReplayReducer(
    animation_data.events,
    animation_data.players,
    initialPitchState,
  )

  const { currentEventIndex, isPlaying } = replayState

  // ─── Player inspection popover ────────────────────────────────────────
  const [selectedPlayer, setSelectedPlayer] = useState<{
    player: AnimationPlayer
    position: { x: number; y: number }
    status: "standing" | "prone" | "stunned"
  } | null>(null)

  const handlePlayerClick = useCallback(
    (playerId: string, rect: DOMRect) => {
      const player = animation_data.players[playerId]
      if (!player) return
      let status: "standing" | "prone" | "stunned" = "standing"
      for (const pp of replayState.pitchState.playerPositions.values()) {
        if (pp.playerId === playerId) {
          status = pp.status
          break
        }
      }
      setSelectedPlayer({
        player,
        position: { x: rect.left + rect.width / 2, y: rect.top },
        status,
      })
    },
    [animation_data.players, replayState.pitchState],
  )

  const handlePopoverClose = useCallback(() => {
    setSelectedPlayer(null)
  }, [])

  // ─── Narrative-to-token hover cross-linking ───────────────────────────
  const [highlightedPlayerId, setHighlightedPlayerId] = useState<string | null>(
    null,
  )

  const handlePlayerHover = useCallback((playerId: string) => {
    setHighlightedPlayerId(playerId)
  }, [])

  const handlePlayerHoverEnd = useCallback(() => {
    setHighlightedPlayerId(null)
  }, [])

  // ─── Player name lookup for narrative hover cross-links ──────────────
  const playerLookup = useMemo(
    () => buildPlayerLookup(animation_data.players),
    [animation_data.players],
  )

  // ─── Mobile layout ───────────────────────────────────────────────────
  const isMobile = useIsMobile()
  const [showNarrative, setShowNarrative] = useState(false)

  // ─── Derived values for the current event ─────────────────────────────

  const actionColor = getActionColor(currentEvent)

  // Determine current half from the latest event's phase, falling back to
  // counting kickoff events for older replays that didn't include a phase.
  const half = useMemo<1 | 2 | "et">(() => {
    const upToNow = animation_data.events.slice(0, currentEventIndex + 1)
    const latestPhase = [...upToNow]
      .reverse()
      .find((e) => e.phase != null)?.phase

    if (latestPhase === "extra_time") return "et"
    if (latestPhase === "first_half") return 1
    if (latestPhase === "second_half") return 2

    const kickoffCount = upToNow.filter(
      (e) => e.highlightType === "kickoff",
    ).length
    return kickoffCount >= 2 ? 2 : 1
  }, [animation_data.events, currentEventIndex])

  const title = `${match.home_team_name} vs ${match.away_team_name} — Replay`

  const narrativePanel = (
    <NarrativePanel
      events={animation_data.events}
      currentEventIndex={currentEventIndex}
      homeTeamId={match.home_team_id}
      awayTeamId={match.away_team_id}
      homeTeamName={match.home_team_name}
      awayTeamName={match.away_team_name}
      homeTeamHue={homeTeamHue}
      awayTeamHue={awayTeamHue}
      onPlayerHover={handlePlayerHover}
      onPlayerHoverEnd={handlePlayerHoverEnd}
      onSeek={seekTo}
      playerLookup={playerLookup}
    />
  )

  return (
    <>
      <Head title={title} />
      <ReplayLayout
        leagueId={league.id}
        matchId={match.id}
        homeTeamName={match.home_team_name}
        awayTeamName={match.away_team_name}
        homeScore={match.home_score}
        awayScore={match.away_score}
        extraTimePlayed={match.extra_time_played}
        homeTeamRace={match.home_team_race}
        awayTeamRace={match.away_team_race}
        homeTeamHue={homeTeamHue}
        awayTeamHue={awayTeamHue}
        turnIndicator={
          <TurnIndicator
            half={half}
            turnLabel={currentEvent?.turnLabel ?? null}
            actingTeamId={currentEvent?.actingTeamId ?? null}
            homeTeamId={match.home_team_id}
            awayTeamId={match.away_team_id}
            homeTeamHue={homeTeamHue}
            awayTeamHue={awayTeamHue}
          />
        }
      >
        {/* Horizontal flex: pitch+controls | narrative panel (desktop) */}
        <div className="flex min-h-0 flex-1">
          {/* Left: pitch area + controls arranged vertically */}
          <div className="flex min-h-0 flex-1 flex-col">
            {/* Pitch grid (flex-1): overflow-hidden prevents grid cells from extending into controls bar */}
            <div className="min-h-0 flex-1 overflow-hidden">
              <PitchGrid
                pitchState={replayState.pitchState}
                homeTeamId={match.home_team_id}
                homeTeamHue={homeTeamHue}
                awayTeamHue={awayTeamHue}
                actingPlayerId={currentEvent?.actingPlayerId ?? null}
                actionColor={actionColor}
                currentEvent={currentEvent ?? null}
                highlightedPlayerId={highlightedPlayerId}
                highlightType={currentEvent?.highlightType ?? null}
                eventKey={currentEventIndex}
                onPlayerClick={handlePlayerClick}
              />
            </div>

            {/* Playback controls bar (fixed height, pinned to bottom) */}
            <PlaybackControls
              isPlaying={isPlaying}
              speed={speed}
              currentEventIndex={currentEventIndex}
              totalEvents={animation_data.events.length}
              events={animation_data.events}
              homeTeamId={match.home_team_id}
              awayTeamId={match.away_team_id}
              homeTeamHue={homeTeamHue}
              awayTeamHue={awayTeamHue}
              onTogglePlay={togglePlay}
              onStepForward={stepForward}
              onStepBack={stepBack}
              onSeek={seekTo}
              onSetSpeed={setSpeed}
              appendSlot={
                isMobile ? (
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setShowNarrative((v) => !v)}
                    className="h-9 w-9 rounded-md transition-colors"
                    style={{
                      color: showNarrative
                        ? "oklch(0.80 0.16 85)"
                        : "oklch(0.65 0.01 230)",
                      background: showNarrative
                        ? "oklch(0.28 0.02 230 / 0.6)"
                        : "oklch(0.18 0.005 230 / 0.5)",
                    }}
                    aria-label={
                      showNarrative ? "Hide play-by-play" : "Show play-by-play"
                    }
                    data-testid="narrative-toggle"
                  >
                    <BookOpen className="size-4" />
                  </Button>
                ) : null
              }
            />
          </div>

          {/* Right: narrative panel — desktop sidebar */}
          {!isMobile && narrativePanel}
        </div>

        {/* Mobile: narrative panel as fixed slide-up overlay */}
        {isMobile && showNarrative && (
          <div
            style={{
              position: "fixed",
              bottom: 0,
              left: 0,
              right: 0,
              height: "50vh",
              zIndex: 40,
              display: "flex",
              flexDirection: "column",
            }}
          >
            {/* Close bar */}
            <Button
              variant="ghost"
              onClick={() => setShowNarrative(false)}
              className="h-auto w-full justify-between rounded-none"
              style={{
                padding: "8px 14px",
                background: "oklch(0.14 0.005 230)",
                borderTop: "2px solid oklch(0.25 0.01 230)",
                color: "oklch(0.65 0.01 230)",
                fontSize: "12px",
                fontWeight: 600,
              }}
              aria-label="Close play-by-play"
            >
              <span>Play-by-Play</span>
              <X className="size-4" />
            </Button>
            <NarrativePanel
              events={animation_data.events}
              currentEventIndex={currentEventIndex}
              homeTeamId={match.home_team_id}
              awayTeamId={match.away_team_id}
              homeTeamName={match.home_team_name}
              awayTeamName={match.away_team_name}
              homeTeamHue={homeTeamHue}
              awayTeamHue={awayTeamHue}
              onPlayerHover={handlePlayerHover}
              onPlayerHoverEnd={handlePlayerHoverEnd}
              onSeek={seekTo}
              playerLookup={playerLookup}
              style={{
                width: "100%",
                borderLeft: "none",
                borderTop: "none",
                flex: 1,
              }}
            />
          </div>
        )}

        {/* Player inspection popover — rendered outside pitch container */}
        <PlayerPopover
          player={selectedPlayer?.player ?? null}
          position={selectedPlayer?.position ?? null}
          status={selectedPlayer?.status ?? null}
          onClose={handlePopoverClose}
        />
      </ReplayLayout>
    </>
  )
}
